import { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ScrollIndicator from "../components/ScrollIndicator";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Country codes data
const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '🇮🇳', minLength: 10, maxLength: 10 },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸', minLength: 10, maxLength: 10 },
  { code: '+44', country: 'UK', flag: '🇬🇧', minLength: 10, maxLength: 11 },
  { code: '+61', country: 'Australia', flag: '🇦🇺', minLength: 9, maxLength: 9 },
  { code: '+971', country: 'UAE', flag: '🇦🇪', minLength: 9, maxLength: 9 },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', minLength: 8, maxLength: 8 },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾', minLength: 9, maxLength: 10 },
  { code: '+49', country: 'Germany', flag: '🇩🇪', minLength: 10, maxLength: 11 },
  { code: '+33', country: 'France', flag: '🇫🇷', minLength: 9, maxLength: 9 },
  { code: '+39', country: 'Italy', flag: '🇮🇹', minLength: 10, maxLength: 10 },
  { code: '+81', country: 'Japan', flag: '🇯🇵', minLength: 10, maxLength: 11 },
  { code: '+86', country: 'China', flag: '🇨🇳', minLength: 11, maxLength: 11 },
  { code: '+82', country: 'South Korea', flag: '🇰🇷', minLength: 9, maxLength: 10 },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', minLength: 9, maxLength: 9 },
  { code: '+974', country: 'Qatar', flag: '🇶🇦', minLength: 8, maxLength: 8 },
  { code: '+968', country: 'Oman', flag: '🇴🇲', minLength: 8, maxLength: 8 },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭', minLength: 8, maxLength: 8 },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼', minLength: 8, maxLength: 8 },
  { code: '+27', country: 'South Africa', flag: '🇿🇦', minLength: 9, maxLength: 9 },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬', minLength: 10, maxLength: 10 },
  { code: '+254', country: 'Kenya', flag: '🇰🇪', minLength: 9, maxLength: 9 },
  { code: '+55', country: 'Brazil', flag: '🇧🇷', minLength: 10, maxLength: 11 },
  { code: '+52', country: 'Mexico', flag: '🇲🇽', minLength: 10, maxLength: 10 },
  { code: '+63', country: 'Philippines', flag: '🇵🇭', minLength: 10, maxLength: 10 },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩', minLength: 10, maxLength: 12 },
  { code: '+66', country: 'Thailand', flag: '🇹🇭', minLength: 9, maxLength: 9 },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳', minLength: 9, maxLength: 10 },
  { code: '+94', country: 'Sri Lanka', flag: '🇱🇰', minLength: 9, maxLength: 9 },
  { code: '+977', country: 'Nepal', flag: '🇳🇵', minLength: 10, maxLength: 10 },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩', minLength: 10, maxLength: 10 },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰', minLength: 10, maxLength: 10 },
  { code: '+7', country: 'Russia', flag: '🇷🇺', minLength: 10, maxLength: 10 },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱', minLength: 9, maxLength: 9 },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭', minLength: 9, maxLength: 9 },
  { code: '+46', country: 'Sweden', flag: '🇸🇪', minLength: 9, maxLength: 9 },
  { code: '+47', country: 'Norway', flag: '🇳🇴', minLength: 8, maxLength: 8 },
  { code: '+45', country: 'Denmark', flag: '🇩🇰', minLength: 8, maxLength: 8 },
  { code: '+358', country: 'Finland', flag: '🇫🇮', minLength: 9, maxLength: 10 },
  { code: '+353', country: 'Ireland', flag: '🇮🇪', minLength: 9, maxLength: 9 },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿', minLength: 9, maxLength: 10 },
];

const AnusthanPage = () => {
  const location = useLocation();
  const { anusthanId } = useParams();
  const navigate = useNavigate();
  const [anusthans, setAnusthans] = useState([]);
  const [anusthansLoading, setAnusthansLoading] = useState(true);
  const [filters, setFilters] = useState({
    deity: 'all',
    tithi: 'all',
    dosha: 'all',
    benefit: 'all'
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', countryCode: '+91', comments: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [testOtp, setTestOtp] = useState('');
  const [parentOrderId, setParentOrderId] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Word count helper for comments (max 60 words)
  const getWordCount = (text) => text.trim() ? text.trim().split(/\s+/).length : 0;
  const commentsWordCount = getWordCount(formData.comments);

  // Get selected country info
  const getSelectedCountry = () => COUNTRY_CODES.find(c => c.code === formData.countryCode) || COUNTRY_CODES[0];
  const isPhoneValid = () => {
    const country = getSelectedCountry();
    return formData.phone.length >= country.minLength && formData.phone.length <= country.maxLength;
  };

  // Fetch anusthans from database
  useEffect(() => {
    const fetchAnusthans = async () => {
      try {
        setAnusthansLoading(true);
        const response = await fetch(`${API_URL}/api/anusthans`);
        const data = await response.json();
        if (data.anusthans && data.anusthans.length > 0) {
          // Map 'image' field to 'image_url' for frontend compatibility
          // Also include og_image for social sharing display
          const mappedAnusthans = data.anusthans.map(a => ({
            ...a,
            image_url: a.image || a.image_url || '',
            og_image: a.og_image || ''
          }));
          setAnusthans(mappedAnusthans);
        }
      } catch (err) {
        console.error('Error fetching anusthans:', err);
      } finally {
        setAnusthansLoading(false);
      }
    };
    fetchAnusthans();
  }, []);

  // Check for pre-selected ceremony and parent order from My Consultation
  useEffect(() => {
    if (anusthans.length === 0) return;
    
    // Check if URL has anusthan slug parameter - auto-select that anusthan
    if (anusthanId) {
      // Try to find by slug (which includes ID at the end)
      const matchingAnusthan = findAnusthanBySlug(anusthanId, anusthans);
      if (matchingAnusthan) {
        setSelectedEvent(matchingAnusthan);
        // Scroll to TOP of page to show hero section with image
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
        return; // Skip other logic if URL has anusthan slug
      }
      
      // Fallback: try parsing as numeric ID for backward compatibility
      const id = parseInt(anusthanId, 10);
      if (!isNaN(id)) {
        const anusthanById = anusthans.find(a => a.id === id);
        if (anusthanById) {
          setSelectedEvent(anusthanById);
          // Scroll to TOP of page to show hero section with image
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
          // Update URL to use new slug format
          navigate(`/anusthan/${generateSlug(anusthanById)}`, { replace: true });
          return;
        }
      }
    }
    
    // Check if coming from My Consultation with a selected ceremony
    if (location.state?.selectedCeremony) {
      // Find the matching anusthan from our local data by ID
      const matchingAnusthan = anusthans.find(a => a.id === location.state.selectedCeremony.id);
      if (matchingAnusthan) {
        setSelectedEvent(matchingAnusthan);
      } else {
        // If no match found, use the passed data but ensure packages exists and map image
        const ceremony = location.state.selectedCeremony;
        if (ceremony.packages && Array.isArray(ceremony.packages)) {
          setSelectedEvent({
            ...ceremony,
            image_url: ceremony.image || ceremony.image_url || '',
            og_image: ceremony.og_image || ''
          });
        }
      }
    }
    
    // Check for customer info passed from My Consultation
    if (location.state?.customerInfo) {
      const { name, phone } = location.state.customerInfo;
      if (name) setFormData(prev => ({ ...prev, name }));
      if (phone) {
        // Extract country code if present
        const matchedCountry = COUNTRY_CODES.find(c => phone.startsWith(c.code));
        if (matchedCountry) {
          const cleanPhone = phone.replace(matchedCountry.code, '');
          setFormData(prev => ({ ...prev, phone: cleanPhone, countryCode: matchedCountry.code }));
        } else {
          const cleanPhone = phone.replace(/^\+\d+/, '');
          setFormData(prev => ({ ...prev, phone: cleanPhone }));
        }
      }
    }
    
    // Check for parent order context from sessionStorage
    const storedParentOrderId = sessionStorage.getItem('parent_order_id');
    const storedName = sessionStorage.getItem('customer_name');
    const storedPhone = sessionStorage.getItem('customer_phone');
    
    if (storedParentOrderId) {
      setParentOrderId(storedParentOrderId);
    }
    
    // Use sessionStorage as fallback for customer info
    if (!location.state?.customerInfo) {
      if (storedName) setFormData(prev => ({ ...prev, name: storedName }));
      if (storedPhone) {
        // Extract country code if present
        const matchedCountry = COUNTRY_CODES.find(c => storedPhone.startsWith(c.code));
        if (matchedCountry) {
          const cleanPhone = storedPhone.replace(matchedCountry.code, '');
          setFormData(prev => ({ ...prev, phone: cleanPhone, countryCode: matchedCountry.code }));
        } else {
          const cleanPhone = storedPhone.replace(/^\+\d+/, '');
          setFormData(prev => ({ ...prev, phone: cleanPhone }));
        }
      }
    }
  }, [location.state, anusthans, anusthanId]);

  // Reset to list view when navigating to this page via header (only if no state and no URL param)
  useEffect(() => {
    if (!location.state?.selectedCeremony && !anusthanId) {
      setSelectedEvent(null);
      setSelectedPackage(null);
      setOtpSent(false);
      setOtpVerified(false);
      setBookingSuccess(false);
      setOtp(['', '', '', '', '', '']);
      // Only reset form if not coming from My Consultation
      if (!sessionStorage.getItem('parent_order_id')) {
        setFormData({ name: '', phone: '', countryCode: '+91', comments: '' });
      }
      setError('');
      setBookingId('');
      setTestOtp('');
    }
  }, [location.key, location.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [selectedEvent, anusthans]); // Added anusthans dependency to re-observe when data loads

  const filteredAnusthans = anusthans.filter(a => {
    if (filters.deity !== 'all' && a.deity.toLowerCase() !== filters.deity) return false;
    if (filters.tithi !== 'all' && a.tithi.toLowerCase() !== filters.tithi) return false;
    if (filters.dosha !== 'all' && !a.doshas.map(d => d.toLowerCase()).includes(filters.dosha)) return false;
    if (filters.benefit !== 'all' && !a.benefits.map(b => b.toLowerCase()).includes(filters.benefit)) return false;
    return true;
  }).sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date ascending

  const resetFilters = () => {
    setFilters({ deity: 'all', tithi: 'all', dosha: 'all', benefit: 'all' });
  };

  // Generate URL-friendly slug from anusthan data
  const generateSlug = (anusthan) => {
    // Use planets if available, otherwise use deity name
    let baseName = '';
    if (anusthan.planets) {
      baseName = anusthan.planets;
    } else if (anusthan.deity) {
      baseName = anusthan.deity;
    } else {
      baseName = anusthan.name;
    }
    
    // Create slug: lowercase, replace spaces/special chars with hyphens, add "anusthan"
    const slug = baseName
      .toLowerCase()
      .replace(/[&]/g, '-and-')
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    // Add anusthan suffix and include ID for uniqueness
    return `${slug}-anusthan-${anusthan.id}`;
  };

  // Find anusthan by slug (matches the slug pattern)
  const findAnusthanBySlug = (slug, anusthansList) => {
    // Extract ID from end of slug (format: name-anusthan-123)
    const idMatch = slug.match(/-(\d+)$/);
    if (idMatch) {
      const id = parseInt(idMatch[1], 10);
      return anusthansList.find(a => a.id === id);
    }
    // Fallback: try to match by generated slug
    return anusthansList.find(a => generateSlug(a) === slug);
  };

  // Helper function to select an anusthan and update URL for shareable links
  const selectAnusthan = (event) => {
    setSelectedEvent(event);
    // Update URL to shareable link with readable slug
    const slug = generateSlug(event);
    navigate(`/anusthan/${slug}`, { replace: true });
    // Scroll to top of page to show anusthan image and title
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper function to go back to list view
  const backToList = () => {
    setSelectedEvent(null);
    setSelectedPackage(null);
    setOtpSent(false);
    setOtpVerified(false);
    setBookingSuccess(false);
    setOtp(['','','','','','']);
    setFormData({name:'',phone:'',countryCode:'+91',comments:''});
    setTermsAccepted(false);
    // Update URL back to main anusthan page
    navigate('/anusthan', { replace: true });
  };

  // Send OTP via WhatsApp API
  const handleSendOtp = async () => {
    if (!formData.name || !isPhoneValid()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/anusthan/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: formData.countryCode + formData.phone,
          name: formData.name
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setOtpSent(true);
        // In test mode, show the OTP for easy testing
        if (data.test_mode && data.test_otp) {
          setTestOtp(data.test_otp);
        }
      } else {
        setError(data.detail || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/anusthan/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: formData.countryCode + formData.phone,
          otp: otpCode
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setOtpVerified(true);
        setTestOtp(''); // Clear test OTP after verification
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDaysAway = (dateStr) => {
    const eventDate = new Date(dateStr);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays < 0) return "Completed";
    return `${diffDays} days away`;
  };

  // Detail View - Matching Reference Design
  if (selectedEvent) {
    const event = selectedEvent;
    
    // Calculate countdown
    const getCountdown = (dateStr) => {
      const eventDate = new Date(dateStr);
      const today = new Date();
      const diffTime = eventDate - today;
      const diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
      const hours = Math.max(0, Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
      const mins = Math.max(0, Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60)));
      return { days: diffDays, hours, mins };
    };
    
    const countdown = getCountdown(event.date);
    
    // Format date for social sharing
    const eventDate = new Date(event.date);
    const formattedDate = eventDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    
    // Generate dynamic page title and meta description for social sharing
    const pageTitle = `Amatya | ${event.name} - Participate Now`;
    const pageDescription = `अमात्य — आपका दिव्य परामर्शदाता | Your Divine Counselor. Participate in ${event.name}. ${event.description ? event.description.substring(0, 150) : 'Sacred Vedic ceremony for spiritual growth and divine blessings.'}`;
    const pageImage = event.image_url?.startsWith('/') ? `${window.location.origin}${event.image_url}` : (event.image_url || `${window.location.origin}/og-image.jpg`);
    const pageUrl = `${window.location.origin}/anusthan/${generateSlug(event)}`;
    
    // OG description with Hindi text and date
    const ogDescription = `Your Divine Counselor.\nParticipate Now - ${event.name}\n${event.devName || ''}\nDate: ${formattedDate}`;
    
    return (
      <div data-testid="anusthan-detail-page" style={{background:'#2E221A', minHeight:'100vh'}}>
        {/* Dynamic Meta Tags for Social Sharing */}
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          
          {/* Open Graph / Facebook / WhatsApp */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:title" content="Amatya | अमात्य — आपका दिव्य परामर्शदाता" />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={pageImage} />
          <meta property="og:site_name" content="Amatya - Your Divine Counselor" />
          <meta property="og:locale" content="en_IN" />
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={pageUrl} />
          <meta name="twitter:title" content="Amatya | अमात्य — आपका दिव्य परामर्शदाता" />
          <meta name="twitter:description" content={ogDescription} />
          <meta name="twitter:image" content={pageImage} />
        </Helmet>
        
        {/* Hero Section - Dark Background */}
        <div data-testid="anusthan-detail-hero" style={{background:'#2E221A', padding:'40px 0 60px'}}>
          <div className="wrap">
            {/* Navigation Row - Back and Share buttons */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'40px'}}>
              {/* Back Button */}
              <button 
                onClick={backToList} 
                data-testid="back-btn"
                style={{
                  fontFamily:'Lato, sans-serif', fontSize:'0.75rem', letterSpacing:'0.1em', 
                  color:'#D4C1A8', background:'none', border:'none', cursor:'pointer', 
                  display:'flex', alignItems:'center', gap:'8px'
                }}
              >
                ← BACK TO ALL ANUSTHANS
              </button>
              
              {/* Share Button */}
              <button 
                onClick={() => {
                  const slug = generateSlug(event);
                  // Use API endpoint for sharing - serves proper OG meta tags for social media crawlers
                  const shareUrl = `${window.location.origin}/api/og/anusthan/${slug}`;
                  navigator.clipboard.writeText(shareUrl).then(() => {
                    alert('Link copied to clipboard! Share this Anusthan with others.');
                  }).catch(() => {
                    // Fallback for older browsers
                    prompt('Copy this link to share:', shareUrl);
                  });
                }}
                data-testid="share-anusthan-btn"
                style={{
                  fontFamily:'Lato, sans-serif', fontSize:'0.75rem', letterSpacing:'0.1em', 
                  color:'#D4A843', background:'rgba(212,168,67,0.1)', border:'1px solid #D4A843', 
                  cursor:'pointer', padding:'8px 16px', borderRadius:'4px',
                  display:'inline-flex', alignItems:'center', gap:'8px'
                }}
              >
                📤 SHARE THIS ANUSTHAN
              </button>
            </div>
            
            {/* Hero Grid - Visual Card + Info */}
            <div className="anusthan-hero-grid">
              {/* Visual Card - Left (First on mobile via CSS) */}
              <div className="anusthan-visual-card" style={{background:'#3D2A1C', borderRadius:'8px', overflow:'hidden', border:'1px solid rgba(212,168,67,0.2)'}}>
                {/* Icon/Image Area */}
                <div style={{background:'#4A3324', padding: event.image_url ? '0' : '60px 40px', display:'flex', alignItems:'center', justifyContent:'center', minHeight: event.image_url ? 'auto' : '200px'}}>
                  {event.image_url ? (
                    <img 
                      src={event.image_url.startsWith('/') ? `${API_URL}${event.image_url}` : event.image_url}
                      alt={event.name}
                      style={{width:'100%', height:'220px', objectFit:'contain', backgroundColor:'#2E221A'}}
                    />
                  ) : (
                    <span style={{fontSize:'6rem'}}>{event.icon}</span>
                  )}
                </div>
                {/* Tithi & Date */}
                <div style={{padding:'24px', textAlign:'center', background:'#E5D3A8'}}>
                  <div style={{fontFamily:'Cinzel, serif', fontSize:'0.9rem', color:'#8B4513', letterSpacing:'0.1em', marginBottom:'8px'}}>{event.tithiLabel}</div>
                  <div style={{fontFamily:'Cinzel, serif', fontSize:'1.4rem', color:'#2E221A', letterSpacing:'0.05em', fontWeight:'500', textTransform:'uppercase'}}>{event.displayDate}</div>
                </div>
                {/* Countdown */}
                <div style={{display:'flex', justifyContent:'center', gap:'20px', padding:'24px', background:'#E5D3A8', borderTop:'1px solid rgba(139,69,19,0.2)'}}>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontFamily:'Cinzel, serif', fontSize:'2rem', color:'#8B4513', fontWeight:'600'}}>{countdown.days}</div>
                    <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.6rem', letterSpacing:'0.15em', color:'#725b47', textTransform:'uppercase'}}>Days</div>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontFamily:'Cinzel, serif', fontSize:'2rem', color:'#8B4513', fontWeight:'600'}}>{String(countdown.hours).padStart(2, '0')}</div>
                    <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.6rem', letterSpacing:'0.15em', color:'#725b47', textTransform:'uppercase'}}>Hours</div>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontFamily:'Cinzel, serif', fontSize:'2rem', color:'#8B4513', fontWeight:'600'}}>{String(countdown.mins).padStart(2, '0')}</div>
                    <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.6rem', letterSpacing:'0.15em', color:'#725b47', textTransform:'uppercase'}}>Mins</div>
                  </div>
                </div>
              </div>
              
              {/* Info - Right (Second on mobile via CSS) */}
              <div className="anusthan-info-section" style={{paddingTop:'20px'}}>
                <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'#D4A843', marginBottom:'16px'}}>
                  {event.planets && event.target_goal ? (
                    <>{event.planets} · {event.target_goal} · {event.planets}</>
                  ) : (
                    <>{event.deity} · {event.tithiLabel}</>
                  )}
                </div>
                <h1 style={{fontFamily:'Cinzel, serif', fontSize:'2.4rem', color:'#F0EAD6', letterSpacing:'0.04em', lineHeight:'1.2', marginBottom:'12px', textTransform:'uppercase'}}>
                  {event.name}
                </h1>
                <div style={{fontFamily:'Noto Serif Devanagari, serif', fontSize:'1.3rem', color:'#D4A843', opacity:'0.7', marginBottom:'20px'}}>
                  {event.devName}
                </div>
                <p style={{fontFamily:'IM Fell English, serif', fontStyle:'italic', fontSize:'1.05rem', lineHeight:'1.85', color:'rgba(240,234,214,0.65)', marginBottom:'28px', maxWidth:'600px'}}>
                  {event.shortDesc}
                </p>
                <div style={{display:'flex', gap:'12px', flexWrap:'wrap'}}>
                  {event.tags.map((tag, i) => (
                    <span key={i} style={{
                      fontFamily:'Lato, sans-serif', fontSize:'0.6rem', letterSpacing:'0.12em', textTransform:'uppercase',
                      padding:'10px 18px', background:'transparent', color:'#D4A843', 
                      border:'1px solid rgba(212,168,67,0.4)'
                    }}>{tag}</span>
                  ))}
                </div>
                
                {/* OG Image is generated dynamically at /api/og/image/{id} for social media sharing only */}
                {/* It includes brand name "AMATYA" + tagline + Hindi anusthan name on divine background */}
              </div>
            </div>
          </div>
        </div>

        {/* Body Section - Content + Booking Panel */}
        <section data-testid="anusthan-detail-body" style={{background:'#FAF7F3', padding:'80px 0'}}>
          <div className="wrap">
            <div className="anusthan-detail-content" style={{display:'grid', gridTemplateColumns:'1fr 400px', gap:'60px', alignItems:'start'}}>
              {/* Booking Panel - Shows first on mobile */}
              <div id="booking-section" className="anusthan-booking-panel reveal d2" style={{position:'sticky', top:'100px', gridColumn:'2', gridRow:'1'}}>
                {!bookingSuccess ? (
                  <div style={{background:'#E5D3A8', border:'1px solid rgba(139,69,19,0.2)', overflow:'hidden', width:'100%', boxSizing:'border-box'}} data-testid="anusthan-whatsapp-cta">
                    {/* Panel Header */}
                    <div style={{background:'#3D2A1C', padding:'24px 20px'}}>
                      <div style={{fontFamily:'Cinzel, serif', fontSize:'1rem', color:'#F0EAD6', letterSpacing:'0.05em', marginBottom:'6px', textTransform:'uppercase'}}>Participate in This Anusthan</div>
                      <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.8rem', fontWeight:'300', color:'rgba(240,234,214,0.6)'}}>Reach us on WhatsApp to register and receive guidance</div>
                    </div>

                    {/* Panel Body — WhatsApp CTA */}
                    <div style={{padding:'24px 20px'}}>
                      <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'#725b47', marginBottom:'14px'}}>Available Packages</div>

                      {event.packages.map((pkg, i) => (
                        <div
                          key={i}
                          data-testid={`package-${pkg.id}`}
                          style={{
                            display:'flex', alignItems:'flex-start', gap:'12px',
                            padding:'14px', background:'#F5EDD8',
                            border:'1px solid rgba(139,69,19,0.2)',
                            marginBottom:'12px',
                            flexWrap:'wrap', boxSizing:'border-box'
                          }}
                        >
                          <div style={{flex:'1', minWidth:'0'}}>
                            <div style={{fontFamily:'Cinzel, serif', fontSize:'0.9rem', color:'#2E221A', letterSpacing:'0.03em', marginBottom:'6px', wordWrap:'break-word'}}>{pkg.name}</div>
                            <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.75rem', fontWeight:'300', color:'#725b47', lineHeight:'1.6', wordWrap:'break-word'}}>{pkg.includes}</div>
                          </div>
                          <div style={{fontFamily:'Cinzel, serif', fontSize:'1rem', color:'#8B4513', fontWeight:'500', flexShrink:'0', whiteSpace:'nowrap'}}>₹{pkg.price.toLocaleString()}</div>
                        </div>
                      ))}

                      <a
                        href={`https://wa.me/917633997555?text=${encodeURIComponent(`Hi Amatya, I would like to book my reading for ${event.name} (${event.devName || ''}) Anusthan on ${event.displayDate || ''}. Please share the next steps.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-book-reading-btn"
                        style={{
                          display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
                          width:'100%', boxSizing:'border-box',
                          background:'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                          color:'#fff', textDecoration:'none',
                          padding:'18px 16px', marginTop:'8px',
                          fontFamily:'Cinzel, serif', fontSize:'0.9rem', fontWeight:'600',
                          letterSpacing:'0.04em', lineHeight:'1.3',
                          boxShadow:'0 6px 20px rgba(18, 140, 126, 0.28)', textAlign:'center'
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
                        Book Your Reading Now!!
                      </a>

                      <p style={{fontFamily:'Lato, sans-serif', fontSize:'0.8rem', fontWeight:'300', color:'#725b47', textAlign:'center', marginTop:'14px', lineHeight:'1.6'}}>
                        📱 +91 7633 997 555 · We respond personally — usually within a few hours.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{background:'#E5D3A8', padding:'40px 28px', textAlign:'center'}} data-testid="booking-success">
                    <div style={{fontSize:'3rem', marginBottom:'20px'}}>🙏</div>
                    <div style={{fontFamily:'Cinzel, serif', fontSize:'1.4rem', color:'#2E221A', marginBottom:'20px'}}>Booking Confirmed</div>
                    <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.9rem', color:'#4A3324', lineHeight:'1.8'}}>
                      <p><strong>{formData.name}</strong>, your participation in <strong>{event.name}</strong> has been confirmed.</p>
                      <p style={{marginTop:'16px'}}>Package: {selectedPackage.name}<br/>Amount: ₹ {selectedPackage.price.toLocaleString()}<br/>Date: {event.displayDate}<br/>Order ID: {bookingId || `AN-${Date.now()}`}</p>
                      <p style={{marginTop:'16px'}}>All updates and documentation will be sent to your WhatsApp at {formData.countryCode} {formData.phone}</p>
                    </div>
                    {/* Social Media Links */}
                    <div style={{marginTop:'24px', paddingTop:'24px', borderTop:'1px solid rgba(139,69,19,0.2)'}}>
                      <p style={{fontFamily:'Lato, sans-serif', fontSize:'0.8rem', color:'#725b47', marginBottom:'12px'}}>Follow us on</p>
                      <div style={{display:'flex', justifyContent:'center', gap:'20px'}}>
                        <a href="https://www.instagram.com/amatyalive/" target="_blank" rel="noopener noreferrer" style={{color:'#8B4513', fontSize:'1.5rem'}} data-testid="social-instagram">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                        </a>
                        <a href="https://x.com/amatyalive" target="_blank" rel="noopener noreferrer" style={{color:'#8B4513', fontSize:'1.5rem'}} data-testid="social-twitter">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        </a>
                        <a href="https://www.youtube.com/@amatyalive" target="_blank" rel="noopener noreferrer" style={{color:'#8B4513', fontSize:'1.5rem'}} data-testid="social-youtube">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Main Content - Shows after booking panel on mobile */}
              <div className="anusthan-main-content reveal" style={{gridColumn:'1', gridRow:'1'}}>
                <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', letterSpacing:'0.25em', textTransform:'uppercase', color:'#C05C34', display:'block', marginBottom:'16px'}}>ABOUT THIS ANUSTHAN</span>
                <h2 style={{fontFamily:'Cinzel, serif', fontSize:'1.8rem', color:'#2E221A', letterSpacing:'0.04em', marginBottom:'24px'}}>The significance & purpose</h2>
                <p style={{fontFamily:'IM Fell English, serif', fontSize:'1rem', lineHeight:'1.9', color:'#4A3324', marginBottom:'48px'}}>{event.fullDesc}</p>

                <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', letterSpacing:'0.25em', textTransform:'uppercase', color:'#C05C34', display:'block', marginBottom:'16px'}}>BENEFITS</span>
                <ul style={{listStyle:'none', marginBottom:'48px'}}>
                  {event.benefitsList.map((b, i) => (
                    <li key={i} style={{display:'flex', gap:'12px', alignItems:'flex-start', fontFamily:'Lato, sans-serif', fontSize:'0.9rem', fontWeight:'300', lineHeight:'1.8', color:'#4A3324', marginBottom:'12px'}}>
                      <span style={{color:'#C05C34', flexShrink:'0'}}>✦</span>{b}
                    </li>
                  ))}
                </ul>

                <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', letterSpacing:'0.25em', textTransform:'uppercase', color:'#C05C34', display:'block', marginBottom:'16px'}}>RITUAL SEQUENCE</span>
                <ol style={{listStyle:'none', marginBottom:'48px'}}>
                  {event.ritualSteps.map((s, i) => (
                    <li key={i} style={{display:'flex', gap:'16px', alignItems:'flex-start', fontFamily:'Lato, sans-serif', fontSize:'0.9rem', fontWeight:'300', lineHeight:'1.8', color:'#4A3324', marginBottom:'16px', paddingBottom:'16px', borderBottom:'1px solid rgba(184,134,11,0.15)'}}>
                      <span style={{fontFamily:'Cinzel, serif', fontSize:'0.8rem', color:'#B8860B', background:'rgba(184,134,11,0.1)', width:'28px', height:'28px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:'0'}}>{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>

                {/* What You Will Receive */}
                <div style={{background:'#2E221A', padding:'24px', marginTop:'24px'}}>
                  <div style={{fontFamily:'Cinzel, serif', fontSize:'1.1rem', color:'#F0EAD6', letterSpacing:'0.05em', marginBottom:'24px', textTransform:'uppercase'}}>What You Will Receive</div>
                  <ul style={{listStyle:'none'}}>
                    <li style={{display:'flex', gap:'14px', alignItems:'flex-start', fontFamily:'Lato, sans-serif', fontSize:'0.88rem', fontWeight:'300', lineHeight:'1.7', color:'rgba(240,234,214,0.7)', marginBottom:'16px'}}>
                      <span style={{color:'#4CAF50', fontSize:'1.2rem', flexShrink:'0'}}>🎥</span>
                      <span>Full video recording of your Puja — your name and Gotra clearly invoked</span>
                    </li>
                    <li style={{display:'flex', gap:'14px', alignItems:'flex-start', fontFamily:'Lato, sans-serif', fontSize:'0.88rem', fontWeight:'300', lineHeight:'1.7', color:'rgba(240,234,214,0.7)', marginBottom:'16px'}}>
                      <span style={{color:'#4CAF50', fontSize:'1.2rem', flexShrink:'0'}}>📸</span>
                      <span>Photographs throughout the ceremony, delivered via WhatsApp</span>
                    </li>
                    <li style={{display:'flex', gap:'14px', alignItems:'flex-start', fontFamily:'Lato, sans-serif', fontSize:'0.88rem', fontWeight:'300', lineHeight:'1.7', color:'rgba(240,234,214,0.7)', marginBottom:'16px'}}>
                      <span style={{color:'#FFA500', fontSize:'1.2rem', flexShrink:'0'}}>📜</span>
                      <span>Participation certificate with your name and the ceremony details</span>
                    </li>
                    <li style={{display:'flex', gap:'14px', alignItems:'flex-start', fontFamily:'Lato, sans-serif', fontSize:'0.88rem', fontWeight:'300', lineHeight:'1.7', color:'rgba(240,234,214,0.7)', marginBottom:'16px'}}>
                      <span style={{color:'#4CAF50', fontSize:'1.2rem', flexShrink:'0'}}>📱</span>
                      <span>Post-puja guidance sent to your WhatsApp within 24 hours of completion</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Calendar View
  return (
    <div data-testid="anusthan-page">
      {/* Hero */}
      <div className="page-hero" data-testid="anusthan-hero">
        <div className="page-hero-content">
          <span className="page-hero-label">Sacred Pujas & Upcoming Anusthans <span style={{fontFamily:'var(--ff-v)'}}>अनुष्ठान</span></span>
          <h1 className="page-hero-title">Sacred Pujas &<br/><em>Upcoming Anusthans</em></h1>
          <p className="page-hero-sub">Participate in scheduled Vedic rituals, Yagyas and Daan performed by our qualified Brahmins — your name invoked, your intention honoured, everything documented and delivered to you on WhatsApp.</p>
        </div>
      </div>

      {/* Filter Bar */}
      <section className="an-filter-section" style={{background:'#EDE0C0', padding:'24px 0', borderBottom:'1px solid rgba(184,134,11,0.15)'}} data-testid="anusthan-filters">
        <div className="wrap">
          <div className="an-filter-bar" style={{display:'flex', alignItems:'center', gap:'16px', flexWrap:'wrap'}}>
            <span className="an-filter-label" style={{fontFamily:'Lato, sans-serif', fontSize:'0.7rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'#4A2E1A'}}>Filter by</span>
            <select className="an-filter-select" value={filters.deity} onChange={(e) => setFilters({...filters, deity: e.target.value})} data-testid="filter-deity" style={{padding:'12px 40px 12px 16px', fontFamily:'IM Fell English, serif', fontSize:'0.95rem', border:'1px solid rgba(184,134,11,0.3)', background:'#F5EDD8', color:'#1C0F07', cursor:'pointer', outline:'none', minWidth:'140px', appearance:'none', backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23B8860B\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat:'no-repeat', backgroundPosition:'right 12px center'}}>
              <option value="all">All Deities</option>
              <option value="ganesh">Ganesh</option>
              <option value="shiva">Shiva</option>
              <option value="lakshmi">Lakshmi</option>
              <option value="vishnu">Vishnu</option>
              <option value="durga">Durga</option>
              <option value="surya">Surya</option>
              <option value="navagraha">Navagraha</option>
              <option value="pitru">Pitru</option>
            </select>
            <select className="an-filter-select" value={filters.tithi} onChange={(e) => setFilters({...filters, tithi: e.target.value})} data-testid="filter-tithi" style={{padding:'12px 40px 12px 16px', fontFamily:'IM Fell English, serif', fontSize:'0.95rem', border:'1px solid rgba(184,134,11,0.3)', background:'#F5EDD8', color:'#1C0F07', cursor:'pointer', outline:'none', minWidth:'120px', appearance:'none', backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23B8860B\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat:'no-repeat', backgroundPosition:'right 12px center'}}>
              <option value="all">All Tithis</option>
              <option value="chaturthi">Chaturthi</option>
              <option value="ekadashi">Ekadashi</option>
              <option value="purnima">Purnima</option>
              <option value="amavasya">Amavasya</option>
              <option value="pradosh">Pradosh</option>
              <option value="navami">Navami</option>
            </select>
            <select className="an-filter-select" value={filters.dosha} onChange={(e) => setFilters({...filters, dosha: e.target.value})} data-testid="filter-dosha" style={{padding:'12px 40px 12px 16px', fontFamily:'IM Fell English, serif', fontSize:'0.95rem', border:'1px solid rgba(184,134,11,0.3)', background:'#F5EDD8', color:'#1C0F07', cursor:'pointer', outline:'none', minWidth:'130px', appearance:'none', backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23B8860B\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat:'no-repeat', backgroundPosition:'right 12px center'}}>
              <option value="all">All Doshas</option>
              <option value="kaal sarp">Kaal Sarp</option>
              <option value="mangal">Mangal</option>
              <option value="pitru">Pitru</option>
              <option value="shani">Shani</option>
              <option value="grahan">Grahan</option>
            </select>
            <select className="an-filter-select" value={filters.benefit} onChange={(e) => setFilters({...filters, benefit: e.target.value})} data-testid="filter-benefit" style={{padding:'12px 40px 12px 16px', fontFamily:'IM Fell English, serif', fontSize:'0.95rem', border:'1px solid rgba(184,134,11,0.3)', background:'#F5EDD8', color:'#1C0F07', cursor:'pointer', outline:'none', minWidth:'140px', appearance:'none', backgroundImage:'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23B8860B\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")', backgroundRepeat:'no-repeat', backgroundPosition:'right 12px center'}}>
              <option value="all">All Benefits</option>
              <option value="career">Career & Success</option>
              <option value="wealth">Wealth & Prosperity</option>
              <option value="health">Health & Longevity</option>
              <option value="marriage">Marriage & Relationship</option>
              <option value="peace">Peace & Clarity</option>
              <option value="ancestral">Ancestral Blessings</option>
              <option value="obstacle">Obstacle Removal</option>
            </select>
            <button className="an-reset-btn" onClick={resetFilters} data-testid="reset-filters" style={{padding:'0', fontFamily:'Lato, sans-serif', fontSize:'0.7rem', letterSpacing:'0.12em', textTransform:'uppercase', background:'transparent', color:'#8B2E0C', border:'none', cursor:'pointer', marginLeft:'auto', textDecoration:'underline'}}>Reset Filters</button>
            <span className="an-result-count" style={{fontFamily:'Lato, sans-serif', fontSize:'0.75rem', color:'#7A5C3E'}}>{filteredAnusthans.length} ceremonies</span>
          </div>
        </div>
      </section>

      {/* Scroll to Anusthan Grid */}
      <div style={{background:'var(--white)'}} className="section-scroll-indicator-light">
        <ScrollIndicator targetId="anusthan-grid" />
      </div>

      {/* Anusthan Grid */}
      <section className="an-grid-section" id="anusthan-grid" style={{background:'#f3e8d1', padding:'60px 0'}} data-testid="anusthan-grid">
        <div className="wrap">
          {/* Featured Anusthans Section */}
          {(() => {
            const today = new Date().toISOString().split('T')[0];
            const featuredAnusthans = filteredAnusthans
              .filter(a => a.featured && a.target_goal && a.date >= today)
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .slice(0, 3);
            
            if (featuredAnusthans.length > 0) {
              return (
                <>
                  <div style={{marginBottom:'24px'}}>
                    <h2 style={{fontFamily:'Cinzel, serif', fontSize:'1.5rem', color:'#4a3324', textAlign:'center', marginBottom:'8px'}}>Featured Goal-Based Anusthans</h2>
                    <p style={{fontFamily:'IM Fell English, serif', fontSize:'1rem', color:'#725b47', textAlign:'center', fontStyle:'italic'}}>Achieve your life goals through powerful planetary Anusthans</p>
                  </div>
                  <div className="an-grid" style={{marginBottom:'40px'}}>
                    {featuredAnusthans.map((event, i) => (
                      <div key={event.id} className={`an-card featured reveal ${i > 0 ? `d${Math.min(i, 2)}` : ''}`} onClick={() => selectAnusthan(event)} data-testid={`featured-anusthan-card-${event.id}`} style={{background:'#f3e8d1', border:'2px solid #c05c34', overflow:'hidden', cursor:'pointer', display:'flex', flexDirection:'column', transition:'all 0.3s', position:'relative', boxShadow:'0 4px 15px rgba(192,92,52,0.2)'}}>
                        {/* Featured Badge */}
                        <div style={{position:'absolute', top:'12px', left:'12px', padding:'5px 10px', background:'#c05c34', color:'#f3e8d1', fontSize:'0.5rem', letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:'Lato, sans-serif', zIndex:'2', display:'flex', alignItems:'center', gap:'4px'}}>
                          <span>⭐</span> Featured
                        </div>
                        
                        {/* Icon/Image Area with Target Goal inside */}
                        <div style={{background:'#4a3324', padding: event.image_url ? '0' : '40px 20px', display:'flex', alignItems:'center', justifyContent:'center', minHeight:'180px', overflow:'hidden', position:'relative'}}>
                          {event.image_url ? (
                            <img 
                              src={event.image_url.startsWith('/') ? `${API_URL}${event.image_url}` : event.image_url}
                              alt={event.name}
                              style={{width:'100%', height:'180px', objectFit:'cover', objectPosition:'top center'}}
                            />
                          ) : (
                            <span style={{fontSize:'4rem'}}>{event.icon}</span>
                          )}
                          {/* Target Goal Badge - Inside image area at bottom */}
                          {event.target_goal && (
                            <div style={{position:'absolute', bottom:'8px', right:'8px', padding:'5px 10px', background:'rgba(46,34,26,0.85)', color:'#D4AF37', fontSize:'0.5rem', letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:'Lato, sans-serif', borderRadius:'2px', backdropFilter:'blur(4px)'}}>
                              🎯 {event.target_goal}
                            </div>
                          )}
                        </div>
                        
                        {/* Date & Planet Strip */}
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 20px', background:'#725b47', color:'#e8e0d5'}}>
                          <span style={{fontFamily:'Cinzel, serif', fontSize:'0.85rem', fontWeight:'500', letterSpacing:'0.05em', textTransform:'uppercase', color:'#e8e0d5'}}>{event.displayDate}</span>
                          {event.planets && (
                            <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.7rem', letterSpacing:'0.1em', color:'#D4AF37', background:'rgba(212,175,55,0.15)', padding:'3px 8px'}}>
                              🪐 {event.planets}
                            </span>
                          )}
                        </div>
                        
                        {/* Body */}
                        <div style={{padding:'24px 20px', flex:'1', background:'#f3e8d1'}}>
                          <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.6rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'#c05c34', display:'block', marginBottom:'8px'}}>{event.deity}</span>
                          <div style={{fontFamily:'Cinzel, serif', fontSize:'1.1rem', color:'#4a3324', letterSpacing:'0.03em', marginBottom:'12px', lineHeight:'1.3'}}>{event.name}</div>
                          <p style={{fontFamily:'IM Fell English, serif', fontSize:'0.9rem', lineHeight:'1.7', color:'#725b47', marginBottom:'16px'}}>{event.shortDesc}</p>
                          <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                            {event.tags.slice(0, 3).map((tag, j) => <span key={j} style={{fontFamily:'Lato, sans-serif', fontSize:'0.55rem', letterSpacing:'0.1em', textTransform:'uppercase', padding:'5px 12px', border:'1px solid #a48c6f', color:'#725b47', background:'transparent'}}>{tag}</span>)}
                          </div>
                        </div>
                        
                        {/* Footer */}
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderTop:'1px solid #a48c6f', background:'#f3e8d1'}}>
                          <div>
                            <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', color:'#725b47', display:'block'}}>From</span>
                            <span style={{fontFamily:'Cinzel, serif', fontSize:'1rem', color:'#4a3324', letterSpacing:'0.03em'}}>₹ {event.packages[0].price.toLocaleString()}</span>
                          </div>
                          <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.7rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#c05c34', fontWeight:'600'}}>Participate →</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Separator Line */}
                  <div style={{display:'flex', alignItems:'center', gap:'20px', marginBottom:'40px'}}>
                    <div style={{flex:'1', height:'1px', background:'linear-gradient(to right, transparent, #a48c6f)'}}></div>
                    <span style={{fontFamily:'Cinzel, serif', fontSize:'0.85rem', color:'#725b47', letterSpacing:'0.1em'}}>ALL ANUSTHANS</span>
                    <div style={{flex:'1', height:'1px', background:'linear-gradient(to left, transparent, #a48c6f)'}}></div>
                  </div>
                </>
              );
            }
            return null;
          })()}
          
          {/* All Anusthans Grid */}
          {filteredAnusthans.length > 0 ? (
            <div className="an-grid">
              {filteredAnusthans.map((event, i) => {
                const isNextCeremony = i === 0 && !event.featured; // First non-featured card
                return (
                  <div key={event.id} className={`an-card ${isNextCeremony ? 'featured' : ''} reveal ${i > 0 ? `d${Math.min(i, 2)}` : ''}`} onClick={() => selectAnusthan(event)} data-testid={`anusthan-card-${event.id}`} style={{background:'#f3e8d1', border: event.featured ? '2px solid #c05c34' : '1px solid #725b47', overflow:'hidden', cursor:'pointer', display:'flex', flexDirection:'column', transition:'all 0.3s', position:'relative'}}>
                    {/* Badges */}
                    {event.featured && (
                      <div style={{position:'absolute', top:'12px', left:'12px', padding:'5px 10px', background:'#c05c34', color:'#f3e8d1', fontSize:'0.5rem', letterSpacing:'0.12em', textTransform:'uppercase', fontFamily:'Lato, sans-serif', zIndex:'2', display:'flex', alignItems:'center', gap:'4px'}}>
                        <span>⭐</span> Featured
                      </div>
                    )}
                    {!event.featured && isNextCeremony && (
                      <div style={{position:'absolute', top:'16px', left:'16px', padding:'6px 14px', background:'#c05c34', color:'#f3e8d1', fontSize:'0.55rem', letterSpacing:'0.15em', textTransform:'uppercase', fontFamily:'Lato, sans-serif', zIndex:'2'}}>Next Ceremony</div>
                    )}
                    
                    {/* Icon/Image Area with Target Goal inside */}
                    <div style={{background: (isNextCeremony || event.featured) ? '#4a3324' : '#4a3324', padding: event.image_url ? '0' : '40px 20px', display:'flex', alignItems:'center', justifyContent:'center', minHeight:'180px', overflow:'hidden', position:'relative'}}>
                      {event.image_url ? (
                        <img 
                          src={event.image_url.startsWith('/') ? `${API_URL}${event.image_url}` : event.image_url}
                          alt={event.name}
                          style={{width:'100%', height:'180px', objectFit:'cover', objectPosition:'top center'}}
                        />
                      ) : (
                        <span style={{fontSize:'4rem'}}>{event.icon}</span>
                      )}
                      {/* Target Goal Badge - Inside image area at bottom */}
                      {event.target_goal && (
                        <div style={{position:'absolute', bottom:'8px', right:'8px', padding:'5px 10px', background:'rgba(46,34,26,0.85)', color:'#D4AF37', fontSize:'0.5rem', letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:'Lato, sans-serif', borderRadius:'2px', backdropFilter:'blur(4px)'}}>
                          🎯 {event.target_goal}
                        </div>
                      )}
                    </div>
                    
                    {/* Date Strip */}
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 20px', background:'#725b47', color:'#e8e0d5'}}>
                      <span style={{fontFamily:'Cinzel, serif', fontSize:'0.85rem', fontWeight:'500', letterSpacing:'0.05em', textTransform:'uppercase', color:'#e8e0d5'}}>{event.displayDate}</span>
                      {event.planets ? (
                        <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', letterSpacing:'0.08em', color:'#D4AF37', background:'rgba(212,175,55,0.15)', padding:'2px 6px'}}>
                          🪐 {event.planets}
                        </span>
                      ) : (
                        <span style={{fontFamily:'IM Fell English, serif', fontSize:'0.9rem', fontStyle:'italic', color:'#d0c8bb'}}>{event.tithiLabel}</span>
                      )}
                    </div>
                    
                    {/* Body */}
                    <div style={{padding:'24px 20px', flex:'1', background:'#f3e8d1'}}>
                      <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.6rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'#c05c34', display:'block', marginBottom:'8px'}}>{event.deity}</span>
                      <div style={{fontFamily:'Cinzel, serif', fontSize:'1.1rem', color:'#4a3324', letterSpacing:'0.03em', marginBottom:'12px', lineHeight:'1.3'}}>{event.name}</div>
                      <p style={{fontFamily:'IM Fell English, serif', fontSize:'0.9rem', lineHeight:'1.7', color:'#725b47', marginBottom:'16px'}}>{event.shortDesc}</p>
                      <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                        {event.tags.slice(0, 3).map((tag, j) => <span key={j} style={{fontFamily:'Lato, sans-serif', fontSize:'0.55rem', letterSpacing:'0.1em', textTransform:'uppercase', padding:'5px 12px', border:'1px solid #a48c6f', color:'#725b47', background:'transparent'}}>{tag}</span>)}
                      </div>
                    </div>
                    
                    {/* Footer */}
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderTop:'1px solid #a48c6f', background:'#f3e8d1'}}>
                      <div>
                        <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', color:'#725b47', display:'block'}}>From</span>
                        <span style={{fontFamily:'Cinzel, serif', fontSize:'1rem', color:'#4a3324', letterSpacing:'0.03em'}}>₹ {event.packages[0].price.toLocaleString()}</span>
                      </div>
                      <span style={{fontFamily:'Lato, sans-serif', fontSize:'0.7rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#4a3324'}}>Participate →</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="an-no-results" data-testid="no-results">
              <p>No ceremonies match your selected filters. <button onClick={resetFilters}>Reset filters</button></p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AnusthanPage;
